import { ChangeOrderIcon, ContractorsIcon, EditIcon, OutOfStockIcon } from '@/Client/Icons';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css } from '@emotion/react';
import { useTaskModifierDisplayNames } from '@kaiizen/kai-pro-api-react-state';

const CONTAINER_STYLE = css`
  label: CONTAINER_STYLE;
  margin-bottom: 0;

  @media print {
    display: none;
  }
`;

const HEADER_STYLE = css`
  label: HEADER_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #E8E8E8;
  padding: 3px 7px;
  margin-bottom: 0;
  gap: 12px;
  font-size: ${pxToRem(18)};
  font-weight: 400;
  color: #1D1D1D;
  min-height: 36px;
`;

const TITLE_STYLE = css`
  label: TITLE_STYLE;
  flex: 1;
  font-size: ${pxToRem(18)};
  font-weight: 400;
  color: #1D1D1D;
  margin: 0;
`;

const BUTTONS_CONTAINER_STYLE = css`
  label: BUTTONS_CONTAINER_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  flex-wrap: wrap;
  align-items: center;
  padding: ${LAYOUT2.spacing3}px;
  background-color: ${COLORS2.alwaysDark.white};
`;

const BUTTON_BASE_STYLE = css`
  label: BUTTON_BASE_STYLE;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
  ${TYPOGRAPHY2.xSmall}
  font-weight: 500;
  cursor: pointer;
  background-color: ${COLORS2.alwaysDark.white};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BUTTON_GOLD_STYLE = css`
  label: BUTTON_GOLD_STYLE;
  border: 2px solid ${COLORS2.alwaysDark.yellow};
  color: ${COLORS2.alwaysDark.darkYellow};

  &:hover {
    background-color: ${COLORS2.alwaysDark.lightYellow};
  }
`;

const BUTTON_GOLD_ACTIVE_STYLE = css`
  label: BUTTON_GOLD_ACTIVE_STYLE;
  background-color: ${COLORS2.alwaysDark.yellow};
  color: ${COLORS2.alwaysDark.darkYellow};
  font-weight: 600;
`;

const BUTTON_ORANGE_STYLE = css`
  label: BUTTON_ORANGE_STYLE;
  border: 2px solid ${COLORS2.alwaysDark.darkOrange};
  color: ${COLORS2.alwaysDark.darkOrange};

  &:hover {
    background-color: rgba(255, 152, 0, 0.1);
  }
`;

const BUTTON_ORANGE_ACTIVE_STYLE = css`
  label: BUTTON_ORANGE_ACTIVE_STYLE;
  background-color: ${COLORS2.alwaysDark.darkOrange};
  color: ${COLORS2.alwaysDark.white};
  font-weight: 600;
`;

const BUTTON_RED_STYLE = css`
  label: BUTTON_RED_STYLE;
  border: 2px solid ${COLORS2.alwaysDark.red};
  color: ${COLORS2.alwaysDark.red};

  &:hover {
    background-color: ${COLORS2.alwaysDark.offWhiteRed};
  }
`;

const BUTTON_RED_ACTIVE_STYLE = css`
  label: BUTTON_RED_ACTIVE_STYLE;
  background-color: ${COLORS2.alwaysDark.red};
  color: ${COLORS2.alwaysDark.white};
  font-weight: 600;
`;

const BUTTON_GRAY_STYLE = css`
  label: BUTTON_GRAY_STYLE;
  border: 2px solid ${COLORS2.alwaysDark.gray};
  color: ${COLORS2.alwaysDark.darkGray};

  &:hover {
    background-color: ${COLORS2.alwaysDark.lightGray};
  }
`;

const BUTTON_GRAY_ACTIVE_STYLE = css`
  label: BUTTON_GRAY_ACTIVE_STYLE;
  background-color: ${COLORS2.alwaysDark.gray};
  color: ${COLORS2.alwaysDark.white};
  font-weight: 600;
`;

const BUTTON_DISABLED_STYLE = css`
  label: BUTTON_DISABLED_STYLE;
  border: 2px solid ${COLORS2.alwaysDark.lightGray};
  color: ${COLORS2.alwaysDark.lightGray};
  cursor: not-allowed;
  opacity: 0.5;

  &:hover {
    transform: none;
    box-shadow: none;
    background-color: ${COLORS2.alwaysDark.white};
  }

  &:active {
    transform: none;
  }
`;

const BUTTON_OUT_OF_STOCK_STYLE = css`
  label: BUTTON_OUT_OF_STOCK_STYLE;
  border: 2px solid ${COLORS2.alwaysDark.red};
  color: ${COLORS2.alwaysDark.red};

  &:hover {
    background-color: ${COLORS2.alwaysDark.offWhiteRed};
  }
`;

const BUTTON_OUT_OF_STOCK_ACTIVE_STYLE = css`
  label: BUTTON_OUT_OF_STOCK_ACTIVE_STYLE;
  background-color: ${COLORS2.alwaysDark.red};
  color: ${COLORS2.alwaysDark.white};
  font-weight: 600;
`;

interface AttentionSummaryButtonsProps {
  editRequestsCount: number;
  changeOrdersCount: number;
  unassignedContractorsCount: number;
  deferredTasksCount: number;
  tenantRespCount: number;
  outOfStockCount: number;
  onEditRequestsClick: () => void;
  onChangeOrdersFilterClick: () => void;
  onUnassignedContractorsFilterClick: () => void;
  onDeferredTasksFilterClick: () => void;
  onTenantRespFilterClick: () => void;
  onOutOfStockFilterClick: () => void;
  isEditRequestsActive?: boolean;
  isChangeOrdersActive?: boolean;
  isUnassignedContractorsActive?: boolean;
  isDeferredTasksActive?: boolean;
  isTenantRespActive?: boolean;
  isOutOfStockActive?: boolean;
}

export const AttentionSummaryButtons = ({
  editRequestsCount,
  changeOrdersCount,
  unassignedContractorsCount,
  deferredTasksCount,
  tenantRespCount,
  outOfStockCount,
  onEditRequestsClick,
  onChangeOrdersFilterClick,
  onUnassignedContractorsFilterClick,
  onDeferredTasksFilterClick,
  onTenantRespFilterClick,
  onOutOfStockFilterClick,
  isEditRequestsActive = false,
  isChangeOrdersActive = false,
  isUnassignedContractorsActive = false,
  isDeferredTasksActive = false,
  isTenantRespActive = false,
  isOutOfStockActive = false,
}: AttentionSummaryButtonsProps) => {
  const modifierLabels = useTaskModifierDisplayNames();
  return (
    <div css={CONTAINER_STYLE}>
      <div css={HEADER_STYLE}>
        <h2 css={TITLE_STYLE}>Attention Summary</h2>
      </div>
      <div css={BUTTONS_CONTAINER_STYLE}>
        <button
          type='button'
          css={[
            BUTTON_BASE_STYLE,
            BUTTON_GOLD_STYLE,
            isEditRequestsActive && BUTTON_GOLD_ACTIVE_STYLE,
            editRequestsCount === 0 && BUTTON_DISABLED_STYLE,
          ]}
          onClick={onEditRequestsClick}
          disabled={editRequestsCount === 0}
        >
          <EditIcon size={14} />
          <span>Edit Request ({editRequestsCount})</span>
        </button>

        <button
          type='button'
          css={[
            BUTTON_BASE_STYLE,
            BUTTON_ORANGE_STYLE,
            isChangeOrdersActive && BUTTON_ORANGE_ACTIVE_STYLE,
            changeOrdersCount === 0 && BUTTON_DISABLED_STYLE,
          ]}
          onClick={onChangeOrdersFilterClick}
          disabled={changeOrdersCount === 0}
        >
          <ChangeOrderIcon size={14} />
          <span>Change Order ({changeOrdersCount})</span>
        </button>

        <button
          type='button'
          css={[
            BUTTON_BASE_STYLE,
            BUTTON_RED_STYLE,
            isUnassignedContractorsActive && BUTTON_RED_ACTIVE_STYLE,
            unassignedContractorsCount === 0 && BUTTON_DISABLED_STYLE,
          ]}
          onClick={onUnassignedContractorsFilterClick}
          disabled={unassignedContractorsCount === 0}
        >
          <ContractorsIcon size={14} />
          <span>Unassigned ({unassignedContractorsCount})</span>
        </button>

        <button
          type='button'
          css={[
            BUTTON_BASE_STYLE,
            BUTTON_GRAY_STYLE,
            isDeferredTasksActive && BUTTON_GRAY_ACTIVE_STYLE,
            deferredTasksCount === 0 && BUTTON_DISABLED_STYLE,
          ]}
          onClick={onDeferredTasksFilterClick}
          disabled={deferredTasksCount === 0}
        >
          <span>{modifierLabels.deferred} ({deferredTasksCount})</span>
        </button>

        <button
          type='button'
          css={[
            BUTTON_BASE_STYLE,
            BUTTON_GRAY_STYLE,
            isTenantRespActive && BUTTON_GRAY_ACTIVE_STYLE,
            tenantRespCount === 0 && BUTTON_DISABLED_STYLE,
          ]}
          onClick={onTenantRespFilterClick}
          disabled={tenantRespCount === 0}
        >
          <span>{modifierLabels.tenantResponsibility} ({tenantRespCount})</span>
        </button>

        <button
          type='button'
          css={[
            BUTTON_BASE_STYLE,
            BUTTON_OUT_OF_STOCK_STYLE,
            isOutOfStockActive && BUTTON_OUT_OF_STOCK_ACTIVE_STYLE,
            outOfStockCount === 0 && BUTTON_DISABLED_STYLE,
          ]}
          onClick={onOutOfStockFilterClick}
          disabled={outOfStockCount === 0}
        >
          <OutOfStockIcon size={19} />
          <span>Out of Stock ({outOfStockCount})</span>
        </button>
      </div>
    </div>
  );
};
